if (!customElements.get('byte-product-card-media')) {
  class ByteProductCardMedia extends HTMLElement {
    constructor() {
      super();

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandlePointerDown = this.handlePointerDown.bind(this);
      this.boundHandleScroll = this.#updateNavButtons.bind(this);
      this.boundHandlePrev = this.handlePrev.bind(this);
      this.boundHandleNext = this.handleNext.bind(this);
    }

    connectedCallback() {
      if (this.hasAttribute('data-byte-initialized')) return;

      this.cardWrapper = this.closest('.product-card-wrapper');
      this.variantImages = this.cardWrapper?.querySelectorAll('[data-product-card-variant-image]');
      this.track = this.querySelector('[data-product-card-thumbnail-track]');
      this.navGroup = this.querySelector('.product-card-variant-thumbnails__nav-group');
      this.prevButton = this.querySelector('[data-product-card-thumbnail-action="prev"]');
      this.nextButton = this.querySelector('[data-product-card-thumbnail-action="next"]');
      this.thumbnailButtons = this.querySelectorAll('[data-product-card-thumbnail-action="select"]');

      if (!this.variantImages?.length || !this.track || this.thumbnailButtons.length < 2) return;

      this.#ensureActiveVariantImage();

      this.#bindEvents();
      this.#bindThumbnailPreload();
      this.#bindThumbnailImageLoad();
      this.#updateNavButtons();
      this.#observeCardVisibility();
      this.#observeTrackResize();
      this.setAttribute('data-byte-initialized', '');
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.removeAttribute('data-byte-initialized');
    }

    refresh() {
      this.#updateNavButtons();
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-product-card-thumbnail-action]');
      if (!actionTrigger) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const thumbnailAction = actionTrigger.dataset.productCardThumbnailAction;

      if (thumbnailAction === 'select') {
        this.#setActiveImage(actionTrigger.dataset.productCardMediaId);
        this.#setActiveThumbnail(actionTrigger);
        return;
      }

      if (thumbnailAction === 'prev') {
        this.#scrollTrack(-1);
        return;
      }

      if (thumbnailAction === 'next') {
        this.#scrollTrack(1);
      }
    }

    handlePrev(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#scrollTrack(-1);
    }

    handleNext(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#scrollTrack(1);
    }

    handlePointerDown(event) {
      if (!event.target.closest('[data-product-card-thumbnail-action]')) return;

      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    #ensureActiveVariantImage() {
      const hasActiveImage = Array.from(this.variantImages).some((variantImage) =>
        variantImage.classList.contains('is-active')
      );

      if (!hasActiveImage && this.variantImages[0]) {
        this.variantImages[0].classList.add('is-active');
      }
    }

    #setActiveImage(mediaId) {
      if (!mediaId) return;

      for (const variantImage of this.variantImages) {
        const isActive = variantImage.dataset.productCardMediaId === mediaId;

        variantImage.classList.toggle('is-active', isActive);

        if (isActive && !variantImage.complete) {
          this.#preloadVariantImage(mediaId);
        }
      }
    }

    #preloadVariantImage(mediaId) {
      const variantImage = this.#findVariantImage(mediaId);
      if (!variantImage || variantImage.dataset.preloadState === 'done') return;

      if (variantImage.complete) {
        variantImage.dataset.preloadState = 'done';
        return;
      }

      variantImage.dataset.preloadState = 'loading';
      variantImage.loading = 'eager';

      if (variantImage.decode) {
        variantImage.decode().finally(() => {
          variantImage.dataset.preloadState = 'done';
        });
      } else {
        variantImage.addEventListener(
          'load',
          () => {
            variantImage.dataset.preloadState = 'done';
          },
          { once: true }
        );
      }
    }

    #preloadAllVariantImages() {
      for (const thumbnailButton of this.thumbnailButtons) {
        this.#preloadVariantImage(thumbnailButton.dataset.productCardMediaId);
      }
    }

    #findVariantImage(mediaId) {
      return Array.from(this.variantImages).find(
        (variantImage) => variantImage.dataset.productCardMediaId === mediaId
      );
    }

    #setActiveThumbnail(activeButton) {
      for (const thumbnailButton of this.thumbnailButtons) {
        const isActive = thumbnailButton === activeButton;

        thumbnailButton.classList.toggle('is-active', isActive);
        thumbnailButton.setAttribute('aria-pressed', String(isActive));
      }
    }

    #scrollTrack(direction) {
      const firstThumbnail = this.thumbnailButtons[0];
      if (!firstThumbnail) return;

      const trackStyles = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || '0');
      const scrollAmount = firstThumbnail.offsetWidth + gap;

      this.track.scrollBy({
        left: direction * scrollAmount * 2,
        behavior: 'smooth',
      });
    }

    #updateNavButtons() {
      if (!this.prevButton || !this.nextButton || !this.track) return;

      const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
      const hasOverflow = maxScrollLeft > 1;

      this.classList.toggle('has-track-overflow', hasOverflow);

      if (this.navGroup) {
        this.navGroup.hidden = !hasOverflow;
      }

      if (!hasOverflow) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }

      this.prevButton.disabled = this.track.scrollLeft <= 0;
      this.nextButton.disabled = this.track.scrollLeft >= maxScrollLeft - 1;
    }

    #observeCardVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.#preloadAllVariantImages();
        return;
      }

      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            this.#preloadAllVariantImages();
            this.#updateNavButtons();
            this.visibilityObserver.disconnect();
          }
        },
        { rootMargin: '200px' }
      );

      this.visibilityObserver.observe(this.cardWrapper);
    }

    #observeTrackResize() {
      if (!('ResizeObserver' in window) || !this.track) return;

      const thumbnailsContainer = this.querySelector('.product-card-variant-thumbnails');

      this.resizeObserver = new ResizeObserver(() => {
        this.#updateNavButtons();
      });

      this.resizeObserver.observe(this.track);

      if (thumbnailsContainer) {
        this.resizeObserver.observe(thumbnailsContainer);
      }
    }

    #bindThumbnailPreload() {
      for (const thumbnailButton of this.thumbnailButtons) {
        thumbnailButton.addEventListener(
          'pointerenter',
          () => {
            this.#preloadVariantImage(thumbnailButton.dataset.productCardMediaId);
          },
          { passive: true }
        );
      }
    }

    #bindThumbnailImageLoad() {
      if (!this.track) return;

      for (const thumbnailImage of this.track.querySelectorAll('img')) {
        if (thumbnailImage.complete) continue;

        thumbnailImage.addEventListener(
          'load',
          () => {
            this.#updateNavButtons();
          },
          { once: true }
        );
      }
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick, true);
      this.addEventListener('pointerdown', this.boundHandlePointerDown, true);
      this.track.addEventListener('scroll', this.boundHandleScroll, { passive: true });

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.boundHandlePrev, true);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.boundHandleNext, true);
      }
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick, true);
      this.removeEventListener('pointerdown', this.boundHandlePointerDown, true);

      if (this.track) {
        this.track.removeEventListener('scroll', this.boundHandleScroll);
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.boundHandlePrev, true);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.boundHandleNext, true);
      }
    }
  }

  customElements.define('byte-product-card-media', ByteProductCardMedia);
}
